import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError, API } from "../lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import {
  Car, Plus, Settings, Trash2, Pencil, Mail, Save, Loader2, AlertTriangle, Send,
  Paperclip, FileText, Upload, Download, FileSpreadsheet, Users, UserRound,
} from "lucide-react";

const DOC_CATEGORIE = [
  { v: "libretto", l: "Libretto" },
  { v: "assicurazione", l: "Assicurazione" },
  { v: "contratto_noleggio", l: "Contratto noleggio" },
  { v: "bollo", l: "Bollo" },
  { v: "altro", l: "Altro" },
];

const TIPOLOGIE = [
  { v: "macchina", l: "Macchina" },
  { v: "furgone", l: "Furgone" },
  { v: "camion", l: "Camion" },
];
const SOCIETA = [
  { v: "fotovoltaico", l: "Fotovoltaico" },
  { v: "fibra", l: "Fibra" },
];

const EMPTY = {
  tipologia: "macchina", marca_modello: "", targa: "", player_noleggio: "",
  societa_uso: "fotovoltaico", inizio_contratto: "", fine_contratto: "",
  km_contratto: "", importo_rata: "", deposito: "",
  assicurazione: "noleggio", assicurazione_scadenza: "",
  bollo: "noleggio", bollo_scadenza: "",
  km_inizio: "", km_fine: "",
  localizzatore: false, localizzatore_installato: "interno",
  telepass: false, telepass_matricola: "",
  squadra: "", operatore: "",
};

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  return Math.ceil((d - new Date().setHours(0, 0, 0, 0)) / 86400000);
}

function ScadenzaBadge({ label, dateStr }) {
  const days = daysUntil(dateStr);
  if (days === null) return null;
  let color = "#6B7280";
  if (days <= 7) color = "#DC2626";
  else if (days <= 15) color = "#F59E0B";
  else if (days <= 30) color = "#16A34A";
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
      style={{ background: `${color}1A`, color }} data-testid={`scadenza-badge-${label.toLowerCase()}`}>
      {days <= 30 && <AlertTriangle className="w-3 h-3" />}
      {label}: {days < 0 ? "scaduta" : `${days}gg`}
    </span>
  );
}

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>{label}</span>
      {children}
      {hint && <span className="block text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>{hint}</span>}
    </label>
  );
}

/* ---------------- Documenti veicolo ---------------- */
function DocsSection({ vid }) {
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [categoria, setCategoria] = useState("libretto");

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/veicoli/${vid}/files`);
      setDocs(r.data);
    } catch (e) { /* silent */ }
  }, [vid]);

  useEffect(() => { load(); }, [load]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File troppo grande (max 25MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("categoria", categoria);
      fd.append("file", file);
      await api.post(`/veicoli/${vid}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento caricato");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setUploading(false); }
  };

  const download = (d) => {
    const token = localStorage.getItem("access_token") || "";
    window.open(`${API}/veicoli/files/${d.id}/download?auth=${encodeURIComponent(token)}`, "_blank");
  };

  const remove = async (d) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    try {
      await api.delete(`/veicoli/files/${d.id}`);
      setDocs((x) => x.filter((f) => f.id !== d.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const catLabel = (v) => DOC_CATEGORIE.find((c) => c.v === v)?.l || v;

  return (
    <div className="border-t mt-4 pt-4" style={{ borderColor: "var(--border)" }} data-testid="veicolo-docs-section">
      <div className="flex items-center gap-2 mb-3">
        <Paperclip className="w-4 h-4" style={{ color: "var(--primary)" }} />
        <span className="text-sm font-bold" style={{ fontFamily: "Manrope" }}>Documenti del veicolo</span>
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>({docs.length})</span>
      </div>

      <div className="flex flex-wrap items-end gap-2 mb-3">
        <label className="block">
          <span className="text-xs font-semibold" style={{ color: "var(--text-muted)" }}>Tipo documento</span>
          <select className="form-input" value={categoria} onChange={(e) => setCategoria(e.target.value)} data-testid="doc-categoria">
            {DOC_CATEGORIE.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </label>
        <label className="btn-primary px-4 inline-flex items-center gap-2 cursor-pointer" data-testid="doc-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Carica documento
          <input type="file" className="hidden" accept="image/*,application/pdf" onChange={onUpload} disabled={uploading} data-testid="doc-file-input" />
        </label>
        <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>PDF o immagini · max 25MB</span>
      </div>

      {docs.length === 0 ? (
        <div className="text-xs py-2" style={{ color: "var(--text-muted)" }}>Nessun documento allegato.</div>
      ) : (
        <div className="space-y-1.5">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-2 p-2 rounded-lg border" style={{ borderColor: "var(--border)" }} data-testid={`doc-item-${d.id}`}>
              <FileText className="w-4 h-4 shrink-0" style={{ color: "#DC2626" }} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{d.original_filename || "documento"}</div>
                <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>{catLabel(d.categoria)}</div>
              </div>
              <button onClick={() => download(d)} className="p-1.5 rounded-lg hover:bg-black/5" title="Scarica/Apri" data-testid={`doc-download-${d.id}`}><Download className="w-4 h-4" /></button>
              <button onClick={() => remove(d)} className="p-1.5 rounded-lg hover:bg-black/5 hover:text-red-600" title="Elimina" data-testid={`doc-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Form Modal ---------------- */
function VeicoloModal({ open, onClose, initial, playerOptions, squadraOptions, operatoreOptions, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) setForm(initial ? { ...EMPTY, ...initial } : EMPTY);
  }, [open, initial]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.targa.trim()) { toast.error("La targa è obbligatoria"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        importo_rata: parseFloat(form.importo_rata) || 0,
        deposito: parseFloat(form.deposito) || 0,
      };
      if (isEdit) await api.put(`/veicoli/${initial.id}`, payload);
      else await api.post("/veicoli", payload);
      toast.success(isEdit ? "Veicolo aggiornato" : "Veicolo creato");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="veicolo-form-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }}>
            {isEdit ? "Modifica veicolo" : "Nuovo veicolo aziendale"}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Tipologia veicolo">
            <select className="form-input" value={form.tipologia} onChange={(e) => set("tipologia", e.target.value)} data-testid="f-tipologia">
              {TIPOLOGIE.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
            </select>
          </Field>
          <Field label="Marca e Modello">
            <input className="form-input" value={form.marca_modello} onChange={(e) => set("marca_modello", e.target.value)} data-testid="f-marca" />
          </Field>
          <Field label="Targa">
            <input className="form-input" value={form.targa} onChange={(e) => set("targa", e.target.value.toUpperCase())} data-testid="f-targa" />
          </Field>
          <Field label="Player Noleggio" hint={playerOptions.length === 0 ? "Aggiungi le voci in Impostazioni ⚙️" : undefined}>
            <select className="form-input" value={form.player_noleggio} onChange={(e) => set("player_noleggio", e.target.value)} data-testid="f-player">
              <option value="">—</option>
              {playerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Società Uso">
            <select className="form-input" value={form.societa_uso} onChange={(e) => set("societa_uso", e.target.value)} data-testid="f-societa">
              {SOCIETA.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </Field>
          {form.societa_uso === "fotovoltaico" ? (
            <Field label="Squadra assegnata" hint={squadraOptions.length === 0 ? "Aggiungi le squadre in Impostazioni ⚙️" : undefined}>
              <select className="form-input" value={form.squadra || ""} onChange={(e) => set("squadra", e.target.value)} data-testid="f-squadra">
                <option value="">— Non assegnata —</option>
                {(squadraOptions || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          ) : (
            <Field label="Operatore assegnato" hint={operatoreOptions.length === 0 ? "Aggiungi gli operatori in Impostazioni ⚙️" : undefined}>
              <select className="form-input" value={form.operatore || ""} onChange={(e) => set("operatore", e.target.value)} data-testid="f-operatore">
                <option value="">— Non assegnato —</option>
                {(operatoreOptions || []).map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          )}
          <Field label="Km Contratto">
            <input className="form-input" value={form.km_contratto} onChange={(e) => set("km_contratto", e.target.value)} data-testid="f-km-contratto" />
          </Field>
          <Field label="Inizio Contratto">
            <input type="date" className="form-input" value={form.inizio_contratto} onChange={(e) => set("inizio_contratto", e.target.value)} data-testid="f-inizio" />
          </Field>
          <Field label="Fine Contratto">
            <input type="date" className="form-input" value={form.fine_contratto} onChange={(e) => set("fine_contratto", e.target.value)} data-testid="f-fine" />
          </Field>
          <Field label="Importo Rata (€)">
            <input type="number" step="0.01" className="form-input" value={form.importo_rata} onChange={(e) => set("importo_rata", e.target.value)} data-testid="f-rata" />
          </Field>
          <Field label="Deposito (€)">
            <input type="number" step="0.01" className="form-input" value={form.deposito} onChange={(e) => set("deposito", e.target.value)} data-testid="f-deposito" />
          </Field>

          {/* Assicurazione */}
          <Field label="Assicurazione">
            <select className="form-input" value={form.assicurazione} onChange={(e) => set("assicurazione", e.target.value)} data-testid="f-assicurazione">
              <option value="interno">Interno</option>
              <option value="noleggio">Noleggio</option>
            </select>
          </Field>
          {form.assicurazione === "interno" ? (
            <Field label="Scadenza Assicurazione" hint="Avviso email a 30/15/7 giorni">
              <input type="date" className="form-input" value={form.assicurazione_scadenza} onChange={(e) => set("assicurazione_scadenza", e.target.value)} data-testid="f-assicurazione-scadenza" />
            </Field>
          ) : <div className="hidden sm:block" />}

          {/* Bollo */}
          <Field label="Bollo">
            <select className="form-input" value={form.bollo} onChange={(e) => set("bollo", e.target.value)} data-testid="f-bollo">
              <option value="interna">Interna</option>
              <option value="noleggio">Noleggio</option>
            </select>
          </Field>
          {form.bollo === "interna" ? (
            <Field label="Scadenza Bollo" hint="Avviso email a 30/15/7 giorni">
              <input type="date" className="form-input" value={form.bollo_scadenza} onChange={(e) => set("bollo_scadenza", e.target.value)} data-testid="f-bollo-scadenza" />
            </Field>
          ) : <div className="hidden sm:block" />}

          <Field label="Km Inizio">
            <input className="form-input" value={form.km_inizio} onChange={(e) => set("km_inizio", e.target.value)} data-testid="f-km-inizio" />
          </Field>
          <Field label="Km Fine">
            <input className="form-input" value={form.km_fine} onChange={(e) => set("km_fine", e.target.value)} data-testid="f-km-fine" />
          </Field>

          {/* Localizzatore */}
          <Field label="Localizzatore">
            <select className="form-input" value={form.localizzatore ? "si" : "no"} onChange={(e) => set("localizzatore", e.target.value === "si")} data-testid="f-localizzatore">
              <option value="no">No</option>
              <option value="si">Sì</option>
            </select>
          </Field>
          {form.localizzatore ? (
            <Field label="Localizzatore installato">
              <select className="form-input" value={form.localizzatore_installato} onChange={(e) => set("localizzatore_installato", e.target.value)} data-testid="f-localizzatore-installato">
                <option value="interno">Interno</option>
                <option value="noleggio">Noleggio</option>
              </select>
            </Field>
          ) : <div className="hidden sm:block" />}

          {/* Telepass */}
          <Field label="Telepass">
            <select className="form-input" value={form.telepass ? "si" : "no"} onChange={(e) => set("telepass", e.target.value === "si")} data-testid="f-telepass">
              <option value="no">No</option>
              <option value="si">Sì</option>
            </select>
          </Field>
          {form.telepass ? (
            <Field label="Numero Matricola Telepass">
              <input className="form-input" value={form.telepass_matricola} onChange={(e) => set("telepass_matricola", e.target.value)} data-testid="f-telepass-matricola" />
            </Field>
          ) : <div className="hidden sm:block" />}
        </div>

        {isEdit && <DocsSection vid={initial.id} />}

        <div className="flex justify-end gap-2 mt-4">
          <button className="px-4 py-2 rounded-lg border" style={{ borderColor: "var(--border)" }} onClick={onClose} data-testid="veicolo-cancel-btn">Annulla</button>
          <button className="btn-primary px-5 inline-flex items-center gap-2" disabled={saving} onClick={save} data-testid="veicolo-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Config Modal ---------------- */
function OptionEditor({ label, items, onChange, testid }) {
  const [val, setVal] = useState("");
  const add = () => {
    const v = val.trim();
    if (!v) return;
    if ((items || []).includes(v)) { toast.error("Voce già presente"); return; }
    onChange([...(items || []), v]);
    setVal("");
  };
  return (
    <div className="border-t pt-3" style={{ borderColor: "var(--border)" }}>
      <div className="text-sm font-bold mb-2">{label}</div>
      <div className="flex gap-2 mb-2">
        <input className="form-input flex-1" value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder="Aggiungi voce…" data-testid={`${testid}-input`} />
        <button type="button" className="btn-primary px-3" onClick={add} data-testid={`${testid}-add`}><Plus className="w-4 h-4" /></button>
      </div>
      <div className="flex flex-wrap gap-2">
        {(items || []).map((o) => (
          <span key={o} className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded-lg border" style={{ borderColor: "var(--border)" }} data-testid={`${testid}-opt-${o}`}>
            {o}
            <button type="button" onClick={() => onChange((items || []).filter((x) => x !== o))} className="hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
          </span>
        ))}
        {(items || []).length === 0 && <span className="text-xs" style={{ color: "var(--text-muted)" }}>Nessuna voce</span>}
      </div>
    </div>
  );
}

function ConfigModal({ open, onClose, config, onSaved }) {
  const [c, setC] = useState(config);
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => { if (open) { setC(config); setPwd(""); } }, [open, config]);

  const set = (k, v) => setC((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...c, smtp_port: parseInt(c.smtp_port) || 465 };
      if (pwd) payload.smtp_password = pwd; else payload.smtp_password = "";
      const r = await api.put("/veicoli/config", payload);
      toast.success("Impostazioni salvate");
      onSaved(r.data);
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/veicoli/config/test-email", { to_email: c.notify_email });
      toast.success(`Email di test inviata a ${r.data.to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setTesting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="veicoli-config-modal">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Manrope" }}>Impostazioni email, Player, Squadre & Operatori</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="text-sm font-bold flex items-center gap-2"><Mail className="w-4 h-4" /> Email Aruba (SMTP)</div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Host SMTP" hint="es. smtps.aruba.it">
              <input className="form-input" value={c.smtp_host || ""} onChange={(e) => set("smtp_host", e.target.value)} data-testid="cfg-host" />
            </Field>
            <Field label="Porta" hint="465 (SSL) / 587">
              <input type="number" className="form-input" value={c.smtp_port || 465} onChange={(e) => set("smtp_port", e.target.value)} data-testid="cfg-port" />
            </Field>
            <Field label="Utente / Email">
              <input className="form-input" value={c.smtp_user || ""} onChange={(e) => set("smtp_user", e.target.value)} data-testid="cfg-user" />
            </Field>
            <Field label={`Password ${c.has_smtp_password ? "(salvata)" : ""}`} hint="lascia vuoto per non cambiarla">
              <input type="password" className="form-input" value={pwd} onChange={(e) => setPwd(e.target.value)} placeholder={c.has_smtp_password ? "••••••••" : ""} data-testid="cfg-pwd" />
            </Field>
            <Field label="Mittente (From)">
              <input className="form-input" value={c.smtp_from || ""} onChange={(e) => set("smtp_from", e.target.value)} data-testid="cfg-from" />
            </Field>
            <Field label="SSL">
              <select className="form-input" value={c.smtp_ssl ? "si" : "no"} onChange={(e) => set("smtp_ssl", e.target.value === "si")} data-testid="cfg-ssl">
                <option value="si">SSL (465)</option>
                <option value="no">STARTTLS (587)</option>
              </select>
            </Field>
          </div>
          <Field label="Email dove ricevere gli avvisi di scadenza">
            <input className="form-input" value={c.notify_email || ""} onChange={(e) => set("notify_email", e.target.value)} placeholder="amministrazione@tuodominio.it" data-testid="cfg-notify" />
          </Field>
          <button className="text-sm inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border" style={{ borderColor: "var(--border)" }} disabled={testing} onClick={sendTest} data-testid="cfg-test-email">
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Invia email di test
          </button>

          <OptionEditor label='Voci "Player Noleggio"' items={c.player_noleggio_options} onChange={(a) => set("player_noleggio_options", a)} testid="cfg-player" />
          <OptionEditor label='Squadre (per società Fotovoltaico)' items={c.squadra_options} onChange={(a) => set("squadra_options", a)} testid="cfg-squadra" />
          <OptionEditor label='Operatori (per società Fibra)' items={c.operatore_options} onChange={(a) => set("operatore_options", a)} testid="cfg-operatore" />
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button className="px-4 py-2 rounded-lg border" style={{ borderColor: "var(--border)" }} onClick={onClose}>Chiudi</button>
          <button className="btn-primary px-5 inline-flex items-center gap-2" disabled={saving} onClick={save} data-testid="cfg-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Salva
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ---------------- Page ---------------- */
export default function Veicoli() {
  const [veicoli, setVeicoli] = useState([]);
  const [config, setConfig] = useState({ player_noleggio_options: [] });
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [fTipologia, setFTipologia] = useState("all");
  const [fSocieta, setFSocieta] = useState("all");
  const [fSeverita, setFSeverita] = useState("");
  const [fSquadra, setFSquadra] = useState("all");
  const [fOperatore, setFOperatore] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [v, c] = await Promise.all([api.get("/veicoli"), api.get("/veicoli/config")]);
      setVeicoli(v.data);
      setConfig(c.data);
      return v.data;
    } catch (e) {
      toast.error(formatApiError(e));
      return [];
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // apertura scheda da link email (?open=<id>)
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && veicoli.length) {
      const v = veicoli.find((x) => x.id === openId);
      if (v) { setEditing(v); setModalOpen(true); }
      searchParams.delete("open");
      setSearchParams(searchParams, { replace: true });
    }
  }, [veicoli, searchParams, setSearchParams]);

  const openNew = () => { setEditing(null); setModalOpen(true); };
  const openEdit = (v) => { setEditing(v); setModalOpen(true); };

  const totali = useMemo(() => {
    const rata = veicoli.reduce((s, v) => s + (Number(v.importo_rata) || 0), 0);
    const dep = veicoli.reduce((s, v) => s + (Number(v.deposito) || 0), 0);
    return { rata, annua: rata * 12, dep };
  }, [veicoli]);

  const eur = (n) => n.toLocaleString("it-IT", { style: "currency", currency: "EUR" });

  const vehicleSeverity = (v) => {
    const cand = [];
    if (v.assicurazione === "interno" && v.assicurazione_scadenza) cand.push(daysUntil(v.assicurazione_scadenza));
    if (v.bollo === "interna" && v.bollo_scadenza) cand.push(daysUntil(v.bollo_scadenza));
    if (!cand.length) return null;
    const d = Math.min(...cand);
    if (d > 30) return null;
    return d <= 7 ? "red" : d <= 15 ? "yellow" : "green";
  };
  const squadraOptions = config.squadra_options || [];
  const operatoreOptions = config.operatore_options || [];

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return veicoli.filter((v) => {
      if (q && !(`${v.targa} ${v.marca_modello}`.toLowerCase().includes(q))) return false;
      if (fTipologia !== "all" && v.tipologia !== fTipologia) return false;
      if (fSocieta !== "all" && v.societa_uso !== fSocieta) return false;
      if (fSquadra !== "all" && (v.squadra || "") !== fSquadra) return false;
      if (fOperatore !== "all" && (v.operatore || "") !== fOperatore) return false;
      if (fSeverita && vehicleSeverity(v) !== fSeverita) return false;
      return true;
    });
  }, [veicoli, search, fTipologia, fSocieta, fSquadra, fOperatore, fSeverita]);

  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const params = {};
      if (search.trim()) params.q = search.trim();
      if (fTipologia !== "all") params.tipologia = fTipologia;
      if (fSocieta !== "all") params.societa = fSocieta;
      if (fSquadra !== "all") params.squadra = fSquadra;
      if (fOperatore !== "all") params.operatore = fOperatore;
      if (fSeverita) params.severita = fSeverita;
      const r = await api.get("/veicoli/export/xlsx", { params, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `veicoli_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setExporting(false); }
  };

  const remove = async (v) => {
    if (!window.confirm(`Eliminare il veicolo ${v.targa}?`)) return;
    try {
      await api.delete(`/veicoli/${v.id}`);
      toast.success("Veicolo eliminato");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="veicoli-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl" style={{ background: "var(--primary)" }}><Car className="w-6 h-6 text-white" /></div>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "Manrope" }}>Veicoli Aziendali</h1>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>{veicoli.length} veicoli · avvisi scadenza a 30/15/7 giorni</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="px-4 py-2 rounded-lg border inline-flex items-center gap-2" style={{ borderColor: "var(--border)" }} onClick={exportXlsx} disabled={exporting || veicoli.length === 0} data-testid="veicoli-export-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4" />} Esporta
          </button>
          <button className="px-4 py-2 rounded-lg border inline-flex items-center gap-2" style={{ borderColor: "var(--border)" }} onClick={() => setCfgOpen(true)} data-testid="veicoli-config-btn">
            <Settings className="w-4 h-4" /> Impostazioni
          </button>
          <button className="btn-primary px-4 inline-flex items-center gap-2" onClick={openNew} data-testid="add-veicolo-btn">
            <Plus className="w-4 h-4" /> Nuovo veicolo
          </button>
        </div>
      </div>

      {!loading && veicoli.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="veicoli-summary">
          {[
            { l: "Veicoli attivi", v: String(veicoli.length), t: "count" },
            { l: "Rata mensile totale", v: eur(totali.rata), t: "rata-mensile" },
            { l: "Costo annuo (rate ×12)", v: eur(totali.annua), t: "rata-annua" },
            { l: "Depositi totali", v: eur(totali.dep), t: "depositi" },
          ].map((s) => (
            <div key={s.t} className="card-shell p-4" data-testid={`summary-${s.t}`}>
              <div className="text-xs" style={{ color: "var(--text-muted)" }}>{s.l}</div>
              <div className="text-xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{s.v}</div>
            </div>
          ))}
        </div>
      )}

      {!loading && veicoli.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="veicoli-filters">
          <input
            className="form-input flex-1 min-w-[200px]" placeholder="Cerca per targa o modello…"
            value={search} onChange={(e) => setSearch(e.target.value)} data-testid="veicoli-search"
          />
          <select className="form-input w-auto" value={fTipologia} onChange={(e) => setFTipologia(e.target.value)} data-testid="filter-tipologia">
            <option value="all">Tutte le tipologie</option>
            {TIPOLOGIE.map((t) => <option key={t.v} value={t.v}>{t.l}</option>)}
          </select>
          <select className="form-input w-auto" value={fSocieta} onChange={(e) => setFSocieta(e.target.value)} data-testid="filter-societa">
            <option value="all">Tutte le società</option>
            {SOCIETA.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
          </select>
          <select className="form-input w-auto" value={fSquadra} onChange={(e) => setFSquadra(e.target.value)} data-testid="filter-squadra">
            <option value="all">Tutte le squadre</option>
            <option value="">— Non assegnati —</option>
            {squadraOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="form-input w-auto" value={fOperatore} onChange={(e) => setFOperatore(e.target.value)} data-testid="filter-operatore">
            <option value="all">Tutti gli operatori</option>
            <option value="">— Non assegnati —</option>
            {operatoreOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          <div className="flex items-center gap-1" data-testid="filter-severita">
            {[
              { v: "red", c: "#DC2626", bg: "#FEE2E2", l: "≤7gg" },
              { v: "yellow", c: "#B45309", bg: "#FEF3C7", l: "≤15gg" },
              { v: "green", c: "#15803D", bg: "#DCFCE7", l: "≤30gg" },
            ].map((s) => {
              const active = fSeverita === s.v;
              return (
                <button
                  key={s.v}
                  onClick={() => setFSeverita(active ? "" : s.v)}
                  className="px-2.5 py-2 rounded-lg border inline-flex items-center gap-1 text-sm font-semibold"
                  style={active ? { background: s.bg, borderColor: s.c, color: s.c } : { borderColor: "var(--border)", color: "var(--text-muted)" }}
                  data-testid={`filter-severita-${s.v}`}
                >
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: s.c }} /> {s.l}
                </button>
              );
            })}
          </div>
          {(search || fTipologia !== "all" || fSocieta !== "all" || fSquadra !== "all" || fOperatore !== "all" || fSeverita) && (
            <button onClick={() => { setSearch(""); setFTipologia("all"); setFSocieta("all"); setFSquadra("all"); setFOperatore("all"); setFSeverita(""); }}
              className="text-sm px-2 py-2 hover:underline" style={{ color: "var(--text-muted)" }} data-testid="filter-reset">
              Azzera filtri
            </button>
          )}
          <span className="text-xs ml-auto" style={{ color: "var(--text-muted)" }}>{filtered.length} di {veicoli.length}</span>
        </div>
      )}

      {loading ? (
        <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : veicoli.length === 0 ? (
        <div className="card-shell p-12 text-center" style={{ color: "var(--text-muted)" }}>
          <Car className="w-10 h-10 mx-auto mb-3 opacity-40" />
          Nessun veicolo. Clicca "Nuovo veicolo" per aggiungerne uno.
        </div>
      ) : filtered.length === 0 ? (
        <div className="card-shell p-12 text-center" style={{ color: "var(--text-muted)" }}>
          Nessun veicolo corrisponde ai filtri.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((v) => (
            <div key={v.id} className="card-shell p-4 cursor-pointer" onClick={() => openEdit(v)} data-testid={`veicolo-card-${v.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-bold text-lg" style={{ fontFamily: "Manrope" }}>{v.marca_modello || "Veicolo"}</div>
                  <div className="text-sm font-mono font-semibold">{v.targa || "—"}</div>
                </div>
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button className="p-1.5 rounded-lg hover:bg-black/5" onClick={() => openEdit(v)} data-testid={`veicolo-edit-${v.id}`}><Pencil className="w-4 h-4" /></button>
                  <button className="p-1.5 rounded-lg hover:bg-black/5 hover:text-red-600" onClick={() => remove(v)} data-testid={`veicolo-delete-${v.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "var(--bg)", color: "var(--text-muted)" }}>
                  {TIPOLOGIE.find((t) => t.v === v.tipologia)?.l}
                </span>
                <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "var(--bg)", color: "var(--text-muted)" }}>
                  {SOCIETA.find((s) => s.v === v.societa_uso)?.l}
                </span>
                {v.player_noleggio && <span className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: "var(--bg)", color: "var(--text-muted)" }}>{v.player_noleggio}</span>}
                {v.squadra && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1" style={{ background: "#EEF2FF", color: "#3730A3" }} data-testid={`veicolo-squadra-${v.id}`}>
                    <Users className="w-3 h-3" /> {v.squadra}
                  </span>
                )}
                {v.operatore && (
                  <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold inline-flex items-center gap-1" style={{ background: "#ECFDF5", color: "#065F46" }} data-testid={`veicolo-operatore-${v.id}`}>
                    <UserRound className="w-3 h-3" /> {v.operatore}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {v.assicurazione === "interno" && <ScadenzaBadge label="Assic." dateStr={v.assicurazione_scadenza} />}
                {v.bollo === "interna" && <ScadenzaBadge label="Bollo" dateStr={v.bollo_scadenza} />}
              </div>
              {v.importo_rata > 0 && <div className="text-xs mt-2" style={{ color: "var(--text-muted)" }}>Rata: € {v.importo_rata.toFixed(2)}</div>}
            </div>
          ))}
        </div>
      )}

      <VeicoloModal open={modalOpen} onClose={() => setModalOpen(false)} initial={editing}
        playerOptions={config.player_noleggio_options || []} squadraOptions={squadraOptions} operatoreOptions={operatoreOptions} onSaved={load} />
      <ConfigModal open={cfgOpen} onClose={() => setCfgOpen(false)} config={config} onSaved={setConfig} />
    </div>
  );
}
